import { useState } from "react";
import { Icon } from "./Icon";
import { useI18n } from "../lib/i18n";
import { withBase } from "../lib/base";
import type { Activity } from "../types";

/**
 * "It is not working with my child" — the one question the printed guide cannot
 * answer.
 *
 * The guide is written for the average child of that stage. The parent in front
 * of it has a specific one, who is bored, or two years ahead, or refusing. Until
 * now the only options were to abandon the activity or to improvise, and a
 * parent new to home education does not yet trust their own improvisation.
 *
 * Five buttons, no free-text box. That is a deliberate limit, not an unfinished
 * feature: a text box invites the general parenting question this programme has
 * no business answering through a model, while a fixed set of predicaments
 * keeps every answer anchored to the activity already on screen.
 */

type Mode = "simpler" | "harder" | "bored" | "refused" | "explain";

const MODES: { id: Mode; key: string; icon: string }[] = [
  { id: "simpler", key: "ai.simpler", icon: "spark" },
  { id: "harder", key: "ai.harder", icon: "arrow" },
  { id: "bored", key: "ai.bored", icon: "clock" },
  { id: "refused", key: "ai.refused", icon: "message" },
  { id: "explain", key: "ai.explain", icon: "book" },
];

export function ActivityHelp({ activity }: { activity: Activity }) {
  const { t, locale } = useI18n();
  const [busy, setBusy] = useState<Mode | null>(null);
  const [answer, setAnswer] = useState<string | null>(null);
  const [problem, setProblem] = useState("");
  // 503 means the key is not set on this deployment. The panel then removes
  // itself rather than offering a button that cannot work.
  const [off, setOff] = useState(false);

  async function ask(mode: Mode) {
    setBusy(mode);
    setProblem("");
    setAnswer(null);
    try {
      const response = await fetch(withBase("/api/activity-help"), {
        method: "POST",
        headers: { "content-type": "application/json" },
        // The activity is sent with the request rather than looked up by id:
        // the model must adapt exactly the words the parent is reading, which
        // for an Urdu family are the translated ones.
        body: JSON.stringify({
          mode,
          locale,
          title: activity.title,
          area: activity.area,
          stage: activity.stage,
          duration_minutes: activity.duration_minutes,
          prompt: activity.prompt,
          what_to_do: activity.what_to_do,
          say_this: activity.say_this,
          observe_for: activity.observe_for,
        }),
      });
      if (response.status === 503) {
        setOff(true);
        return;
      }
      const data = (await response.json().catch(() => ({}))) as { text?: string; message?: string };
      if (!response.ok || !data.text) {
        setProblem(data.message || t("ai.unavailable"));
        return;
      }
      setAnswer(data.text);
    } catch {
      setProblem(t("ai.unavailable"));
    } finally {
      setBusy(null);
    }
  }

  if (off) return null;

  return (
    <div className="activity-help">
      <small>{t("ai.helpTitle")}</small>
      <div className="activity-help-modes">
        {MODES.map((mode) => (
          <button
            key={mode.id}
            className="chip-button"
            disabled={busy !== null}
            onClick={() => void ask(mode.id)}
          >
            <Icon name={mode.icon} size={13} /> {t(mode.key)}
          </button>
        ))}
      </div>

      {busy && (
        <p className="activity-help-status">
          <Icon name="spark" size={14} /> {t("ai.thinking")}
        </p>
      )}
      {problem && <p className="activity-help-status problem">{problem}</p>}

      {answer && (
        <div className="activity-help-answer">
          {answer
            .split("\n")
            .map((line) => line.trim())
            .filter(Boolean)
            .map((line, index) => (
              <p key={index}>{line}</p>
            ))}
          {/* Said every time, not once in a settings page. A parent acting on a
              suggestion about their own child should be reminded, at the moment
              they read it, that a model wrote it. */}
          <small className="activity-help-note">{t("ai.disclaimer")}</small>
        </div>
      )}
    </div>
  );
}
